import { type Action, createReducer, on } from '@ngrx/store';

import {
  type CropData,
  type FileItem,
  getUploadStateKey,
  type ResourceResult,
  type UploadState,
} from '../../../domain/public-api';

import {
  addFilesFromComputer,
  confirmUpload,
  errorEncoding,
  errorUploads,
  fileRetry,
  finishEncoding,
  removeAllFiles,
  removeFile,
  resetFilesState,
  revokeUploadConfirmation,
  saveImageEdit,
  setEncodingProgress,
  setUploadFileProgress,
  startEncoding,
  startImageEdit,
  startUpload,
  undoImageEdit,
  uploadFile,
  uploadFinished,
  waitForUpload,
  waitForUploadError,
} from './files.actions';

export const filesFeatureKey = 'files';

export interface FilesState {
  files: FileItem[];
  itemToEdit?: number;
  isUploadConfirmed: boolean;
}

export const initialState: FilesState = {
  files: [],
  isUploadConfirmed: false,
};

const filesReducerFn = createReducer<FilesState>(
  initialState,
  on(resetFilesState, () => ({ ...initialState })),

  on(addFilesFromComputer, (state, { fileItems }) => addFileItem(state, fileItems)),

  on(removeAllFiles, (state) => ({ ...state, files: [] })),
  on(removeFile, (state, { fileId }) => ({
    ...state,
    files: state.files.filter((item) => item.id !== fileId),
  })),

  on(startImageEdit, (state, { fileId }) => ({ ...state, itemToEdit: fileId })),
  on(saveImageEdit, (state, { cropData }) => saveCropData(state, cropData)),
  on(undoImageEdit, (state, { fileId }) => removeCropData(state, fileId)),

  on(confirmUpload, (state) => ({ ...state, isUploadConfirmed: true })),
  on(revokeUploadConfirmation, (state) => ({ ...state, isUploadConfirmed: false })),

  on(startUpload, (state, { fileItems }) => initializeFileUpload(state, fileItems)),
  on(uploadFile, (state, { data, resourceId }) => startFileUpload(state, data, resourceId)),
  on(setUploadFileProgress, (state, { resourceId, progress }) =>
    updateUploadProgress(state, resourceId, progress),
  ),
  on(errorUploads, (state, { fileIds }) => uploadErrors(state, fileIds)),
  on(waitForUpload, (state, { fileId, resourceId }) =>
    waitForUploadByBackend(state, fileId, resourceId),
  ),
  on(waitForUploadError, (state, { resourceId }) => waitForUploadByBackendError(state, resourceId)),
  on(uploadFinished, (state, { resourceId }) => finishFileUpload(state, resourceId)),

  on(startEncoding, (state, { resourceId }) => initializeFileEncoding(state, resourceId)),
  on(setEncodingProgress, (state, { resourceId, progress }) =>
    updateEncodingProgress(state, resourceId, progress),
  ),
  on(errorEncoding, (state, { resourceId }) => errorFileEncoding(state, resourceId)),
  on(finishEncoding, (state, { resourceId, resource }) =>
    finishFileEncoding(state, resourceId, resource),
  ),

  on(fileRetry, (state, { fileId }) => clearUploadData(state, fileId)),
);

export function filesReducer(state: FilesState | undefined, action: Action): FilesState {
  return filesReducerFn(state, action);
}

function addFileItem(state: FilesState, fileItems: FileItem[]): FilesState {
  return { ...state, files: [...fileItems, ...state.files] };
}

function saveCropData(state: FilesState, cropData: CropData): FilesState {
  const fileIndex = state.files.findIndex((item) => item.id === state.itemToEdit);

  if (fileIndex === -1) {
    return state;
  }

  const files = [...state.files];
  files[fileIndex] = {
    ...files[fileIndex],
    cropData,
  };

  if (files[fileIndex].upload?.uploadStage === 'START_UPLOAD') {
    // If the non-edited file has just initialized, move the state to the edited image
    files[fileIndex] = {
      ...files[fileIndex],
      upload: undefined,
      uploadEdited: {
        uploadStage: 'START_UPLOAD',
      },
    };
  }

  return { ...state, files };
}

function removeCropData(state: FilesState, fileId: number): FilesState {
  const fileIndex = state.files.findIndex((item) => item.id === fileId);

  if (fileIndex === -1) {
    return state;
  }

  const files = [...state.files];

  if (
    !files[fileIndex].upload?.uploadStage &&
    files[fileIndex]?.uploadEdited?.uploadStage === 'START_UPLOAD'
  ) {
    // If the edited file has just initialized, move the state to the non-edited image
    files[fileIndex] = {
      ...files[fileIndex],
      upload: {
        uploadStage: 'START_UPLOAD',
      },
      uploadEdited: undefined,
    };
  }

  files[fileIndex] = { ...files[fileIndex], cropData: undefined, uploadEdited: undefined };

  return { ...state, files };
}

function initializeFileUpload(state: FilesState, fileItems: FileItem[]): FilesState {
  const files = state.files.map((item) => {
    const fileItem = fileItems.find((fi) => fi.id === item.id);

    if (!fileItem) {
      return item;
    }

    const isEditedUpload = !!fileItem.cropData;
    const uploadKey: keyof FileItem = isEditedUpload ? 'uploadEdited' : 'upload';

    return { ...fileItem, [uploadKey]: { uploadStage: 'START_UPLOAD' } as UploadState };
  });

  return { ...state, files };
}

function startFileUpload(state: FilesState, file: File | Blob, resourceId: string): FilesState {
  const fileNormalIndex = state.files.findIndex((item) => item.file === file);
  const fileEditedIndex = state.files.findIndex((item) => item?.cropData?.file === file);

  if (fileNormalIndex === -1 && fileEditedIndex === -1) {
    return state;
  }

  const isEditedResource = fileEditedIndex !== -1;
  const uploadKey: keyof FileItem = isEditedResource ? 'uploadEdited' : 'upload';
  const fileIndex = isEditedResource ? fileEditedIndex : fileNormalIndex;

  const files = [...state.files];
  files[fileIndex] = {
    ...state.files[fileIndex],
    [uploadKey]: {
      uploadStage: 'UPLOADING',
      resourceId,
      uploadProgress: 0,
    },
  };

  return { ...state, files };
}

function updateUploadProgress(state: FilesState, resourceId: string, progress: number): FilesState {
  return updateUploadState(state, resourceId, { uploadProgress: progress });
}

function uploadErrors(state: FilesState, fileIds: number[]): FilesState {
  return {
    ...state,
    isUploadConfirmed: false,
    files: state.files.map((item) => {
      if (!fileIds.includes(item.id)) {
        return item;
      }

      const uploadKey = getUploadStateKey(item);

      return {
        ...item,
        [uploadKey]: {
          ...item[uploadKey],
          uploadStage: 'ERROR',
        } as UploadState,
      };
    }),
  };
}

function waitForUploadByBackend(state: FilesState, fileId: number, resourceId: string): FilesState {
  const fileIndex = state.files.findIndex((item) => item.id === fileId);

  if (fileIndex === -1) {
    return state;
  }

  const files = [...state.files];
  files[fileIndex] = {
    ...state.files[fileIndex],
    upload: {
      uploadStage: 'WAITING_FOR_UPLOAD',
      resourceId,
      uploadProgress: 0,
    },
  };

  return { ...state, files };
}

function waitForUploadByBackendError(state: FilesState, resourceId: string): FilesState {
  const files = state.files.filter((item) => item.upload?.resourceId !== resourceId);

  return { ...state, files };
}

function finishFileUpload(state: FilesState, resourceId: string): FilesState {
  return updateUploadState(state, resourceId, { uploadStage: 'UPLOADED', uploadProgress: 100 });
}

function initializeFileEncoding(state: FilesState, resourceId: string): FilesState {
  return updateUploadState(state, resourceId, { uploadStage: 'ENCODING', encodingProgress: 0 });
}

function updateEncodingProgress(
  state: FilesState,
  resourceId: string,
  progress: number,
): FilesState {
  return updateUploadState(state, resourceId, { encodingProgress: progress });
}

function errorFileEncoding(state: FilesState, resourceId: string): FilesState {
  return {
    ...updateUploadState(state, resourceId, {
      uploadStage: 'ERROR',
      uploadProgress: 0,
      encodingProgress: 0,
    }),
    isUploadConfirmed: false,
  };
}

function finishFileEncoding(
  state: FilesState,
  resourceId: string,
  resource: ResourceResult,
): FilesState {
  return updateUploadState(state, resourceId, {
    uploadStage: 'ENCODED',
    encodingProgress: 100,
    resource,
  });
}

// Helper function for partial upload state updates
function updateUploadState(
  state: FilesState,
  resourceId: string,
  uploadStateUpdates: Partial<UploadState>,
): FilesState {
  const fileNormalIndex = state.files.findIndex((item) => item.upload?.resourceId === resourceId);
  const fileEditedIndex = state.files.findIndex(
    (item) => item.uploadEdited?.resourceId === resourceId,
  );

  if (fileNormalIndex === -1 && fileEditedIndex === -1) {
    return state;
  }

  const isEditedResource = fileEditedIndex !== -1;
  const uploadKey: keyof FileItem = isEditedResource ? 'uploadEdited' : 'upload';
  const fileIndex = isEditedResource ? fileEditedIndex : fileNormalIndex;

  const files = [...state.files];
  files[fileIndex] = {
    ...state.files[fileIndex],
    [uploadKey]: {
      ...state.files[fileIndex][uploadKey],
      ...uploadStateUpdates,
    },
  };

  return { ...state, files };
}

function clearUploadData(state: FilesState, fileId: number): FilesState {
  const fileIndex = state.files.findIndex((item) => item.id === fileId);

  if (fileIndex === -1) {
    return state;
  }

  const files = [...state.files];
  const isEditedUpload = !!files[fileIndex].cropData;
  const uploadKey: keyof FileItem = isEditedUpload ? 'uploadEdited' : 'upload';

  files[fileIndex] = {
    ...state.files[fileIndex],
    [uploadKey]: undefined,
  };

  return { ...state, files };
}
